import { orderPrintingConfigService } from 'components/settings-options-and-extensions/components/order-printing-config/infra';
import { getDiffFromNow } from 'shared/utils/date-time';
import OrdersUtils from 'utils/OrdersUtils';
import { FeatureAdoptionTypes } from '../domain';

import type { OrderDetailState, PartnerId } from 'shared/domain';
import type { FeatureAdoptionData } from '../domain';

export function orderDetailAdoptionService(currentOrder: OrderDetailState) {
  const _isNewOrder = OrdersUtils.isNew(currentOrder?.partner_order_state);
  const _showPrinter = !(
    _isNewOrder && OrdersUtils.isMarketPlace(currentOrder?.delivery_method)
  );
  const _STORAGE_ITEM_NAME = 'order_detail_adoption';
  const _DEFAULT_VIEW_VALUE = 1;
  const _DEFAULT_LAST_SHOWN_VALUE = new Date().toISOString();
  const storeId = currentOrder?.store_id;
  const partnerId: PartnerId = currentOrder?.partner_id;
  const hasHandoff = Boolean(currentOrder?.qr_information) && !_isNewOrder;
  const isPrintUnset =
    !orderPrintingConfigService().getOrderPrintingConfig().command &&
    _showPrinter;
  const MAX_VIEWS = 2;

  const _initialAdoptionValue = (type: FeatureAdoptionTypes) => ({
    type,
    views: _DEFAULT_VIEW_VALUE,
    lastShown: _DEFAULT_LAST_SHOWN_VALUE,
  });

  const getStoredData = (): FeatureAdoptionData[] => {
    const parsedItem = JSON.parse(
      localStorage.getItem(_STORAGE_ITEM_NAME) || '[]',
    );
    return parsedItem as FeatureAdoptionData[];
  };

  const updateStoredData = (data: FeatureAdoptionData[]) => {
    const storedData = data.map(val => {
      let views = val.views;
      let lastShown = val.lastShown;

      if (val.views < MAX_VIEWS) {
        views = MAX_VIEWS;
      }

      if (hasDayElapsed(lastShown)) {
        views = _DEFAULT_VIEW_VALUE;
        lastShown = _DEFAULT_LAST_SHOWN_VALUE;
      }
      return {
        ...val,
        views,
        lastShown,
      };
    });

    localStorage.setItem(_STORAGE_ITEM_NAME, JSON.stringify(storedData));
  };

  const hasDayElapsed = (lastShown: string) => {
    const { hours } = getDiffFromNow(lastShown);
    return hours >= 24;
  };

  const addInitialStoredData = () => {
    const initialValues: Record<FeatureAdoptionTypes, FeatureAdoptionData> = {
      [FeatureAdoptionTypes.ScalePrinted]: _initialAdoptionValue(
        FeatureAdoptionTypes.ScalePrinted,
      ),
      [FeatureAdoptionTypes.handoff]: _initialAdoptionValue(
        FeatureAdoptionTypes.handoff,
      ),
    };

    const storedData: FeatureAdoptionData[] = [
      ...(isPrintUnset
        ? [initialValues[FeatureAdoptionTypes.ScalePrinted]]
        : []),
      ...(hasHandoff ? [initialValues[FeatureAdoptionTypes.handoff]] : []),
    ];

    if (storedData.length > 0) {
      localStorage.setItem(_STORAGE_ITEM_NAME, JSON.stringify(storedData));
    }
  };

  const addStoredData = (type: FeatureAdoptionTypes) => {
    const storedData = getStoredData();

    const initialValues: Record<
      FeatureAdoptionTypes,
      ReturnType<typeof _initialAdoptionValue>
    > = {
      [FeatureAdoptionTypes.ScalePrinted]: _initialAdoptionValue(
        FeatureAdoptionTypes.ScalePrinted,
      ),
      [FeatureAdoptionTypes.handoff]: _initialAdoptionValue(
        FeatureAdoptionTypes.handoff,
      ),
    };

    const valueToAdd = initialValues[type];
    if (valueToAdd !== undefined) {
      storedData.push(valueToAdd);
    }

    localStorage.setItem(_STORAGE_ITEM_NAME, JSON.stringify(storedData));
  };

  return {
    getStoredData,
    updateStoredData,
    hasDayElapsed,
    addInitialStoredData,
    addStoredData,
    hasHandoff,
    isPrintUnset,
    MAX_VIEWS,
    storeId,
    partnerId,
  };
}
